import { useEffect } from "react";

const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Cipher & Row",
  "legalName": "CIPHERANDROW LLC",
  "url": "https://www.cipherandrow.com",
  "logo": "https://www.cipherandrow.com/favicon.png",
  "description": "Canada-first carrier and broker verification, monitoring, and compliance for the freight industry.",
  "foundingDate": "2025",
  "contactPoint": {
    "@type": "ContactPoint",
    "email": "support@cipherandrow.com",
    "contactType": "customer support"
  },
  "founder": [
    {
      "@type": "Person",
      "name": "John Nowlan",
      "jobTitle": "Co-Founder"
    },
    {
      "@type": "Person",
      "name": "Louis-Charles Gagnon",
      "jobTitle": "Co-Founder"
    }
  ],
  "sameAs": [
    "https://www.linkedin.com/company/cipherandrow"
  ]
};

const RETURN_POLICY = {
  "@type": "MerchantReturnPolicy",
  "applicableCountry": ["US", "CA"],
  "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
  "merchantReturnDays": 14,
  "returnMethod": "https://schema.org/ReturnByMail",
  "returnFees": "https://schema.org/FreeReturn"
};

const SHIPPING_DETAILS = {
  "@type": "OfferShippingDetails",
  "shippingRate": {
    "@type": "MonetaryAmount",
    "value": "0",
    "currency": "USD"
  },
  "shippingDestination": [
    { "@type": "DefinedRegion", "addressCountry": "US" },
    { "@type": "DefinedRegion", "addressCountry": "CA" }
  ],
  "deliveryTime": {
    "@type": "ShippingDeliveryTime",
    "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" },
    "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" }
  }
};

const PRODUCT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Cipher & Row Verification Platform",
  "description": "Carrier and broker verification with continuous monitoring. Know the moment authority, bond, insurance, or safety status changes. US and Canada coverage.",
  "url": "https://www.cipherandrow.com",
  "image": "https://www.cipherandrow.com/og/home.jpg",
  "brand": {
    "@type": "Brand",
    "name": "Cipher & Row"
  },
  "offers": [
    {
      "@type": "Offer",
      "name": "Carrier Verified+",
      "price": "89",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "89",
        "priceCurrency": "USD",
        "unitCode": "MON"
      },
      "description": "Optional paid carrier tier. Daily monitoring of insurance, authority, and bond status, plus document fraud scans. The free carrier tier stays $0 forever with unlimited broker lookups.",
      "url": "https://www.cipherandrow.com/pricing",
      "hasMerchantReturnPolicy": RETURN_POLICY,
      "shippingDetails": SHIPPING_DETAILS
    },
    {
      "@type": "Offer",
      "name": "Dispatcher Solo",
      "price": "149",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "149",
        "priceCurrency": "USD",
        "unitCode": "MON"
      },
      "description": "Carrier and broker lookup with monitoring alerts. Know before you dispatch.",
      "url": "https://www.cipherandrow.com/pricing",
      "hasMerchantReturnPolicy": RETURN_POLICY,
      "shippingDetails": SHIPPING_DETAILS
    },
    {
      "@type": "Offer",
      "name": "Broker Essential",
      "price": "449",
      "priceCurrency": "USD",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "449",
        "priceCurrency": "USD",
        "unitCode": "MON"
      },
      "description": "Carrier lookup and monitoring alerts. Get notified when a carrier's authority, CVOR, CTQ, NSC, or insurance changes.",
      "url": "https://www.cipherandrow.com/pricing",
      "hasMerchantReturnPolicy": RETURN_POLICY,
      "shippingDetails": SHIPPING_DETAILS
    }
  ]
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Cipher & Row?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cipher & Row verifies carriers and brokers against FMCSA, CVOR, CTQ, and NSC registries, then monitors them for compliance changes. Canada-first. Alerts when authority, bond, or insurance changes."
      }
    },
    {
      "@type": "Question",
      "name": "Do you verify Canadian carriers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, and the depth is published province by province. Every Canadian lookup runs federal authority plus cross-border FMCSA. Provincial coverage: full National Safety Code in British Columbia and Manitoba, Ontario's published rated-carrier list refreshed daily, and Quebec's CTQ safety watch-list refreshed weekly. Carriers outside those provincial lists resolve at the federal cross-border layer."
      }
    },
    {
      "@type": "Question",
      "name": "What does verification check?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Authority status, insurance on file, bond status, safety rating, fleet size, out-of-service history, fraud signals, and trust tier. US runs through FMCSA. Canada runs through CVOR, CTQ, and NSC."
      }
    },
    {
      "@type": "Question",
      "name": "How much does it cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Carriers are free with unlimited broker lookups. Dispatchers are $149/mo Solo or $299/mo Pro. Brokers are $449/mo Essential, $1,199/mo Pro, $2,999/mo Scale, or custom-priced Enterprise. Every paid plan starts with a 14-day free trial, no card, capped at 100 credits. Annual billing is 20% off."
      }
    },
    {
      "@type": "Question",
      "name": "Can I integrate via API?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. An MCP server exposes eight read-only verification tools to AI agents, so Claude, Cursor, or any MCP client can verify a carrier or broker directly. Setup takes about 90 seconds at cipherandrow.com/mcp. REST API access is included on higher plans. See the pricing page for which tiers."
      }
    },
    {
      "@type": "Question",
      "name": "Do you support brokers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Brokers get carrier lookup and monitoring starting at $449/mo (Essential), with a 14-day free trial. Carriers and dispatchers can look up broker bond status, authority, and insurance for free."
      }
    },
    {
      "@type": "Question",
      "name": "We already pay for a carrier vetting platform. Why add Cipher & Row?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Existing carrier vetting platforms are built so a broker can onboard a carrier: one direction, US-focused, and finished when the packet is complete. Cipher & Row adds the other directions. It scores brokers on the same 0-100 ladder using active authority, live BMC-84 surety bond status, and sanctions screening, and gives carriers and dispatchers unlimited broker lookups. The carrier tier is free forever, so onboarding costs your carriers nothing. Canadian coverage is published province by province. Every check seals into a record with an independent RFC 3161 timestamp you can hand to an insurer or a cargo-claim adjuster. Run it alongside your current tool for 14 days, no card required."
      }
    },
    {
      "@type": "Question",
      "name": "How is this better than pulling FMCSA data myself?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It is the same FMCSA data plus four things a direct pull cannot give you. History: a pull shows today's state, not that an authority was revoked and re-registered under a new MC number, or that the same officer appears on several others. Additional sources: OFAC, UN and Canadian consolidated sanctions lists run on every verification, alongside CVOR, CTQ, NSC and CBSA. Continuous monitoring: authority, bond, insurance and safety are watched daily and alert you, instead of waiting for your next manual check. And an evidence record: each check seals with an independent RFC 3161 timestamp, which a browser screenshot is not. A live re-verify can also return fewer verified signals than a stored record, because different sources publish on different cadences."
      }
    },
    {
      "@type": "Question",
      "name": "Is there a free tier?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Carriers are free forever: unlimited broker lookups and a registry profile, no card. Dispatchers and brokers get a 14-day free trial on any plan, no card, capped at 100 credits. After that a paid plan is required. One-time lookups are also free at cipherandrow.com/verify with no signup. Paid plans start at $89/mo for carriers (Verified+) or $149/mo for dispatchers (Solo)."
      }
    }
  ]
};

function injectSchema(id: string, schema: object) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
}

function removeSchema(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

export function MarketingHead() {
  useEffect(() => {
    injectSchema("ld-marketing-org", ORGANIZATION_SCHEMA);
    injectSchema("ld-marketing-product", PRODUCT_SCHEMA);
    injectSchema("ld-marketing-faq", FAQ_SCHEMA);

    return () => {
      removeSchema("ld-marketing-org");
      removeSchema("ld-marketing-product");
      removeSchema("ld-marketing-faq");
    };
  }, []);

  return null;
}
